import { NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import logo from '../../logo.jpg';
import API_BASE_URL from '../../config';
import { completeLogout } from '../../utils/auth';
import { useLang } from '../../i18n/LanguageContext';

const MOBILE_BP = 640;

const NAV_ITEMS = [
  { to: '/analytics', icon: 'bi-speedometer2', key: 'nav.dashboard' },
  { to: '/orders', icon: 'bi-receipt', key: 'nav.orders' },
  { to: '/dishes', icon: 'bi-grid-1x2-fill', key: 'nav.dishes' },
  { to: '/tables', icon: 'bi-layout-wtf', key: 'nav.tables' },
  { to: '/coupons', icon: 'bi-tag-fill', key: 'nav.promotions' },
  { to: '/settings', icon: 'bi-gear-fill', key: 'nav.settings' },
];

export default function Sidebar({ activeOrderCount = 0, mobileOpen = false, onMobileClose, onCollapsedChange }) {
  const { t } = useLang();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BP);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    onCollapsedChange?.(next);
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    const token = localStorage.getItem('ownerToken');
    // Best-effort backend revoke — don't block logout if it fails.
    if (token) {
      try {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 4000
        });
      } catch { /* ignore */ }
    }
    completeLogout();
  };

  // On mobile: x = -260 (hidden) or 0 (visible)
  // On desktop: x = 0 always (visible), collapse via width
  const xPos = isMobile ? (mobileOpen ? 0 : -260) : 0;
  const showLabels = isMobile ? true : !collapsed;

  return (
    <motion.aside
      className={`sidebar ${!isMobile && collapsed ? 'sidebar-collapsed' : ''}`}
      animate={{ x: xPos }}
      initial={{ x: isMobile ? -260 : -260 }}
      transition={{ type: 'spring', stiffness: 300, damping: 35 }}
      style={{ zIndex: isMobile ? 300 : 200 }}
    >
      {/* Logo */}
      <div className="sidebar-logo">
        <img src={logo} alt="Logo" className="sidebar-logo-img" />
        <AnimatePresence>
          {showLabels && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="sidebar-logo-text"
            >
              <span className="sidebar-brand">{t('nav.brand')}</span>
              <span className="sidebar-sub">{t('nav.brandSub')}</span>
            </motion.div>
          )}
        </AnimatePresence>
        {!isMobile && (
          <button className="sidebar-toggle" onClick={toggleCollapsed}>
            <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
          </button>
        )}
      </div>

      {/* Nav Items */}
      <nav className="sidebar-nav">
        {NAV_ITEMS.map((item, i) => (
          <motion.div
            key={item.to}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 + 0.1 }}
          >
            <NavLink
              to={item.to}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              title={!showLabels ? t(item.key) : undefined}
              onClick={() => { if (isMobile) onMobileClose?.(); }}
            >
              <i className={`bi ${item.icon} sidebar-icon`}></i>
              <AnimatePresence>
                {showLabels && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="sidebar-label"
                  >
                    {t(item.key)}
                  </motion.span>
                )}
              </AnimatePresence>
              {item.to === '/orders' && activeOrderCount > 0 && showLabels && (
                <motion.span
                  className="sidebar-badge"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500 }}
                >
                  {activeOrderCount > 99 ? '99+' : activeOrderCount}
                </motion.span>
              )}
            </NavLink>
          </motion.div>
        ))}
      </nav>

      {/* Logout */}
      <div className="sidebar-footer">
        <button className="sidebar-logout" onClick={handleLogout} disabled={loggingOut} title={!showLabels ? t('nav.logout') : undefined}>
          <i className={`bi ${loggingOut ? 'bi-arrow-clockwise sidebar-icon-spin' : 'bi-box-arrow-right'} sidebar-icon`}></i>
          <AnimatePresence>
            {showLabels && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="sidebar-label"
              >
                {t('nav.logout')}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );
}
